import { useEffect, useState, type FormEvent, type WheelEvent } from 'react';
import { createVehiculo, updateVehiculo, type UpsertVehiculoInput } from '../../lib/storage';
import { getSucursales } from '../../lib/storage';
import type { Vehiculo, EtiquetaVehiculo, TipoGasolinaVehiculo, Sucursal } from '../../lib/appTypes';

interface Props {
  initialVehiculo?: Vehiculo | null;
  onSaved: (vehiculo: Vehiculo) => void;
  onCancel: () => void;
}

const ETIQUETAS: Array<{ value: EtiquetaVehiculo; label: string }> = [
  { value: '', label: 'Sin etiqueta' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
  { value: 'averia', label: 'Avería' },
  { value: 'taller', label: 'Taller' },
];

const TIPOS_GASOLINA: Array<{ value: TipoGasolinaVehiculo; label: string }> = [
  { value: 'extra', label: 'Extra' },
  { value: 'super', label: 'Súper' },
  { value: 'diesel', label: 'Diésel' },
];

function preventNumberInputWheelChange(event: WheelEvent<HTMLInputElement>): void {
  event.currentTarget.blur();
}

export default function FormVehiculo({ initialVehiculo, onSaved, onCancel }: Props) {
  const [form, setForm] = useState({
    placa: initialVehiculo?.placa ?? '',
    marca: initialVehiculo?.marca ?? '',
    modelo: initialVehiculo?.modelo ?? '',
    anio: initialVehiculo?.anio ?? new Date().getFullYear(),
    tipoGasolina: initialVehiculo?.tipoGasolina || 'extra',
    kilometrosGalon: initialVehiculo?.kilometrosGalon ?? '',
    precioGalon: initialVehiculo?.precioGalon ?? '',
    etiqueta: initialVehiculo?.etiqueta ?? '',
    sucursalId: initialVehiculo?.sucursal?.id ?? '',
  });
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    void getSucursales()
      .then(response => {
        if (!cancelled) {
          setSucursales(response);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('No se pudieron cargar las sucursales.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function handle(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const payload: UpsertVehiculoInput = {
      placa: form.placa.trim().toUpperCase(),
      marca: form.marca.trim(),
      modelo: form.modelo.trim(),
      anio: Number(form.anio),
      tipoGasolina: form.tipoGasolina as TipoGasolinaVehiculo,
      kilometrosGalon: Number(form.kilometrosGalon),
      precioGalon: Number(form.precioGalon),
      etiqueta: form.etiqueta as EtiquetaVehiculo,
      sucursalId: form.sucursalId || null,
      conductorId: form.etiqueta ? null : initialVehiculo?.conductor?.id ?? null,
    };

    try {
      const vehiculo = initialVehiculo
        ? await updateVehiculo(initialVehiculo.id, payload)
        : await createVehiculo(payload);
      onSaved(vehiculo);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudo guardar el vehículo.');
    } finally {
      setLoading(false);
    }
  }

  const inputCls =
    'w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100';
  const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500';
  const assignmentLocked = Boolean(form.etiqueta);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-stone-900">
          {initialVehiculo ? 'Editar vehículo' : 'Crear vehículo'}
        </h3>
        <p className="text-sm text-stone-500">
          Los vehículos con etiqueta de novedad no pueden quedar asignados a un conductor.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Placa</label>
          <input name="placa" value={form.placa} onChange={handle} required className={inputCls} placeholder="ABC-1234" />
        </div>
        <div>
          <label className={labelCls}>Marca</label>
          <input name="marca" value={form.marca} onChange={handle} required className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Modelo</label>
          <input name="modelo" value={form.modelo} onChange={handle} required className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Año</label>
          <input name="anio" type="number" min={1990} max={new Date().getFullYear() + 1} value={form.anio} onWheel={preventNumberInputWheelChange} onChange={handle} required className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Etiqueta</label>
          <select name="etiqueta" value={form.etiqueta} onChange={handle} className={inputCls}>
            {ETIQUETAS.map(option => (
              <option key={option.value || 'sin-etiqueta'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Tipo de gasolina</label>
          <select name="tipoGasolina" value={form.tipoGasolina} onChange={handle} className={inputCls}>
            {TIPOS_GASOLINA.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Kilómetros por galón</label>
          <input name="kilometrosGalon" type="number" min="0.000001" step="0.000001" value={form.kilometrosGalon} onWheel={preventNumberInputWheelChange} onChange={handle} required className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Precio por galón</label>
          <input name="precioGalon" type="number" min="0.000001" step="0.000001" value={form.precioGalon} onWheel={preventNumberInputWheelChange} onChange={handle} required className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Sucursal</label>
          <select name="sucursalId" value={form.sucursalId} onChange={handle} className={inputCls}>
            <option value="">Sin sucursal</option>
            {sucursales.map(sucursal => (
              <option key={sucursal.id} value={sucursal.id}>
                {sucursal.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>

      {assignmentLocked && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Para asignar este vehículo, primero quita la etiqueta de mantenimiento, avería o taller.
        </div>
      )}

      {initialVehiculo?.conductor && !assignmentLocked && (
        <div className="rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-700">
          La asignación del conductor se administra desde la vista de autos del panel administrativo.
        </div>
      )}

      {initialVehiculo?.motivoNoDisponible === 'pico_y_placa' && !initialVehiculo.conductor && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Este vehículo no está disponible para asignación en este momento por restricción de pico y placa en Quito.
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="ui-btn ui-btn-secondary"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="ui-btn ui-btn-primary"
        >
          {loading ? 'Guardando...' : 'Guardar vehículo'}
        </button>
      </div>
    </form>
  );
}
